import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Brain, Sigma, Shapes, Lock, PlayCircle, Timer } from "lucide-react";

const META = {
  verbal: { icon: Brain, label: "Ragionamento Verbale" },
  numerical: { icon: Sigma, label: "Ragionamento Numerico" },
  abstract: { icon: Shapes, label: "Ragionamento Astratto" },
};

export default function TestList() {
  const [cats, setCats] = useState([]);
  const [hasAll, setHasAll] = useState(false);
  useEffect(() => {
    api.get("/practice").then((r) => { setCats(r.data.categories); setHasAll(r.data.has_all); }).catch(() => {});
  }, []);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="label-mono text-[#002FA7] mb-4">— Allenamento</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-4">Allenati per ogni concorso UE</h1>
        <p className="text-slate-600 max-w-2xl mb-10">
          Test di logica utili per qualunque concorso dell'Unione europea, in particolare per <span className="font-semibold text-[#0A0F1A]">EPSO AD5 Generalist</span>.
          12 domande gratuite per tipo, poi la banca completa da 450 domande con spiegazione.
        </p>

        {/* Simulazione — in evidenza in cima */}
        <div className="border-2 border-[#FFD700] bg-[#0A0F1A] text-white p-8 lg:p-10 premium-grain mb-14 relative overflow-hidden">
          <div className="absolute top-0 right-0 bg-[#FFD700] text-[#0A0F1A] label-mono text-xs px-3 py-1">CONSIGLIATO</div>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 label-mono text-[#FFD700] mb-3"><Timer className="w-5 h-5" />Simulazione cronometrata completa</div>
              <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tight mb-3">Mettiti alla prova come all'esame</h2>
              <p className="text-slate-200 max-w-xl leading-relaxed">
                Tre sezioni a tempo — Verbale <span className="font-mono-num">20/35′</span>, Numerico <span className="font-mono-num">10/20′</span>,
                Astratto <span className="font-mono-num">10/10′</span> — in condizioni d'esame reali, con punteggio e spiegazioni finali.
              </p>
            </div>
            <Link to="/simulazione"><Button data-testid="test-simulation-cta" className="rounded-none bg-[#FFD700] hover:bg-white text-[#0A0F1A] label-mono py-7 px-10 text-base whitespace-nowrap shadow-hard">
              <PlayCircle className="w-5 h-5 mr-2" />Avvia la simulazione
            </Button></Link>
          </div>
        </div>

        <h2 className="font-display font-bold text-2xl mb-6">Allenamento per tipo di logica</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {["verbal", "numerical", "abstract"].map((cat) => {
            const info = cats.find((c) => c.category === cat) || {};
            const Icon = META[cat].icon;
            const unlocked = hasAll || (info.premium_unlocked || 0) > 0;
            return (
              <div key={cat} data-testid={`test-card-${cat}`} className="border border-slate-200 bg-white p-8 flex flex-col hover:shadow-hard transition-all hover:-translate-y-1">
                <Icon className="w-9 h-9 text-[#002FA7] mb-5" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-xl mb-4">{META[cat].label}</h3>
                {unlocked ? (
                  <Link to={`/premium/${cat}`}>
                    <Button data-testid={`test-premium-${cat}`} className="w-full rounded-none bg-[#002FA7] hover:bg-[#001d6b] text-white label-mono py-5 mb-3">
                      <PlayCircle className="w-4 h-4 mr-2" />Allenamento ({hasAll ? info.premium_total : info.premium_unlocked}/{info.premium_total})
                    </Button>
                  </Link>
                ) : (
                  <Link to="/pricing">
                    <Button data-testid={`test-locked-${cat}`} className="w-full rounded-none bg-[#002FA7] hover:bg-[#001d6b] text-white label-mono py-5 mb-3">
                      <Lock className="w-4 h-4 mr-2" />Sblocca {info.premium_total} domande
                    </Button>
                  </Link>
                )}
                <Link to={`/free/${cat}`}>
                  <Button data-testid={`test-free-${cat}`} variant="outline" className="w-full rounded-none border-slate-900 label-mono py-5">
                    12 domande gratuite
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
